import React, { ChangeEvent, useEffect } from 'react'
import { Row, Col } from 'react-grid-system'
import { Controller, useForm } from 'react-hook-form'
import { Trans, useTranslation } from 'react-i18next'
import { object, string } from 'yup'

import { IPreferences } from './PreferencesContainer'
import { ClinTheme } from '../../ClinTheme'
import { ClinPageContentFrame } from '../../components/ClinPageContentFrame'
import { ClinSpacer } from '../../components/ClinSpacer'
import { ClinText } from '../../components/ClinText'
import { TypographyVariant } from '../../components/ClinText/ClinText.styles'
import { ICustomSelectOption } from '../../features/CustomSelect/CustomSelect'

import 'react-tippy/dist/tippy.css'
import { yupResolver } from '@hookform/resolvers/yup'

import {
  getInputPlaceholder,
  PlaceholderType
} from '../../utils/Forms/getInputPlaceholder'
import { ClinSelect, ClinSelectVariant } from '../../components/ClinSelect'
import { UserRole } from '../../constants'
import { StyledSpinnerContainer } from '../OpaCheckout/OpaCheckout.styles'
import { ClinSpinner } from '../../components/ClinSpinner'
import {
  StyledFormClinPageContentFrame,
  StyledHeaderClinPageContentFrame,
  StyledPreferencesHeaderContainer,
  StyledPreferencesHeaderTitle,
  StyledLanguageContainer,
  StyledMarketingContainer,
  StyledClinButton,
  StyledClinGroup,
  StyledClinButtonMobile,
  StyledInviteHeaderNavigation
} from './Preferences.styles'
import { AnnounceMode } from '../../components/ClinAnnounceBar/ClinAnnounceBar'
import { ClinToastBar } from '../../components/ClinToastBar'
import { ClinIcon } from '../../components/ClinIcon'
import { ClinIconPathName } from '../../components/ClinIcon/ClinIcon.paths'

interface IPreferencesProps {
  /** Users preference values */
  preferences: IPreferences
  /** Language options */
  languageOptions: ICustomSelectOption[]
  /** User country */
  userCountry: string
  /** User role */
  userRole?: UserRole
  /** Whether we are loading or not **/
  isLanguageLoading: boolean
  /** When language is changed */
  handleSwitchLanguage: (option: ICustomSelectOption) => void
  /** When change button clicked */
  handleFormSubmit: (data: IPreferenceFormValues) => void
  /** Handle a request to access marketing subscription preferences  */
  handleMarketingSubscriptionRequest: () => void
  /** Cancel form and return to previous page */
  handleCancel: () => void
  /** Navigate to the previous page */
  handleGoBack: () => void
}

export interface IPreferenceFormValues {
  language?: string
}
export const Preferences: React.FC<IPreferencesProps> = ({
  preferences,
  languageOptions,
  isLanguageLoading,
  handleFormSubmit,
  handleMarketingSubscriptionRequest,
  handleCancel,
  handleGoBack
}) => {
  const { t } = useTranslation()

  const defaultValue = {
    language: preferences?.language?.id || ''
  }
  const PreferencesSchema = object().shape({
    language: string()
  })
  const {
    control,
    handleSubmit,
    reset,
    formState: { isDirty }
  } = useForm<IPreferenceFormValues>({
    mode: 'all',
    defaultValues: defaultValue,
    resolver: yupResolver(PreferencesSchema)
  })

  useEffect(() => {
    if (preferences) {
      reset({
        language: preferences?.language?.id || ''
      })
    }
  }, [preferences, reset])

  return (
    <ClinPageContentFrame isFullWidth={true}>
      {isLanguageLoading && (
        <Row justify="center">
          <Col width="auto">
            <StyledSpinnerContainer>
              <ClinSpinner size={ClinTheme.space[7]} />
            </StyledSpinnerContainer>
          </Col>
        </Row>
      )}

      {!isLanguageLoading && (
        <ClinPageContentFrame isFullWidth={true}>
          <StyledPreferencesHeaderContainer className="preferences-header">
            <StyledHeaderClinPageContentFrame>
              <StyledInviteHeaderNavigation>
                <StyledClinButtonMobile
                  onClick={() => handleGoBack()}
                  variant="linkWithIcon"
                >
                  <ClinIcon
                    iconSize={ClinTheme.fontSizes[3]}
                    iconName={ClinIconPathName.ArrowLeft}
                  />
                </StyledClinButtonMobile>
                <StyledPreferencesHeaderTitle variant={TypographyVariant.H3}>
                  {t('preferences:title')}
                </StyledPreferencesHeaderTitle>
              </StyledInviteHeaderNavigation>
            </StyledHeaderClinPageContentFrame>
          </StyledPreferencesHeaderContainer>
          <form
            onSubmit={handleSubmit(async (values) => {
              await handleFormSubmit(values)

              reset({
                language: values.language
              })
            })}
          >
            <StyledFormClinPageContentFrame>
              <StyledLanguageContainer>
                <ClinText
                  fontWeight={ClinTheme.fontWeights.bold}
                  fontSize={ClinTheme.fontSizes[1]}
                  color={ClinTheme.colors.black}
                  marginBottom={ClinTheme.space[3]}
                  lineHeight={ClinTheme.lineHeights.heading[0]}
                >
                  {t('preferences:language')}
                </ClinText>
                <Controller
                  name={'language'}
                  control={control}
                  render={({ field: { value, onChange } }) => (
                    <ClinSelect
                      width="100%"
                      variant={ClinSelectVariant.primaryV2}
                      value={value || ''}
                      onChange={(changeValue: ChangeEvent<HTMLSelectElement>) =>
                        onChange(changeValue.currentTarget.value)
                      }
                    >
                      <option value="" disabled={true}>
                        {getInputPlaceholder(PlaceholderType.SelectInput)}
                      </option>
                      {languageOptions.map(
                        (option: ICustomSelectOption, index: number) => {
                          return (
                            <option key={`code-${index}`} value={option.id}>
                              {option.value}
                            </option>
                          )
                        }
                      )}
                    </ClinSelect>
                  )}
                />
                <ClinSpacer height={ClinTheme.space[5]} />
                <StyledClinGroup>
                  <StyledClinButton
                    type="submit"
                    variant="primary"
                    disabled={!isDirty}
                  >
                    {t('common:buttons.save')}
                  </StyledClinButton>
                  <StyledClinButton onClick={() => handleCancel()}>
                    {t('common:buttons.cancel')}
                  </StyledClinButton>
                </StyledClinGroup>
              </StyledLanguageContainer>
              <StyledMarketingContainer>
                <ClinToastBar
                  height={40}
                  marginTop={0}
                  iconPosition={'top'}
                  mode={AnnounceMode.Information}
                  title={t('preferences:manage_marketing_subscriptions_button')}
                >
                  <Trans
                    i18nKey="preferences:manage_marketing_subscriptions_message"
                    components={{
                      a: (
                        <button
                          type="button"
                          onClick={() => handleMarketingSubscriptionRequest()}
                          style={
                            {
                              cursor: 'pointer',
                              textDecoration: 'underline',
                              color: '#0043CE',
                              background: 'none',
                              border: 'none',
                              padding: 0,
                              font: 'inherit'
                            } as React.CSSProperties
                          }
                        >
                          <></>
                        </button>
                      )
                    }}
                  />
                </ClinToastBar>
              </StyledMarketingContainer>
            </StyledFormClinPageContentFrame>
            {/* Commenting this out because it will be useful when further preferences become available */}
            {/* <Row>
              <Col sm={12} md={10} lg={8}>
                <ClinTable
                  tableHeader={() => {
                    return (
                      <>
                        <ClinTableHeaderCell>
                          <ClinText
                            fontSize={ClinTheme.fontSizes[2]}
                            fontWeight={ClinTheme.fontWeights.medium}
                            color={ClinTheme.colors.primary}
                          >
                            {t('preferences:marketing')}
                          </ClinText>
                        </ClinTableHeaderCell>
                        <ClinTableHeaderCell>
                          <div
                            style={{
                              display: 'inline-flex',
                              alignItems: 'center'
                            }}
                          >
                            {t('preferences:email_me')}
                            <div
                              style={{
                                position: 'relative',
                                marginLeft: '5px',
                                marginTop: '3px'
                              }}
                            >
                              <Tooltip
                                title="sample message"
                                position="top"
                                trigger="click"
                                arrow={true}
                                popperOptions={{
                                  modifiers: {
                                    preventOverflow: {
                                      boundariesElement: 'viewport'
                                    }
                                  }
                                }}
                              >
                                <ClinIcon
                                  iconSize={ClinTheme.fontSizes[3]}
                                  iconName={ClinIconPathName.InfoOutline}
                                />
                              </Tooltip>
                            </div>
                          </div>
                        </ClinTableHeaderCell>
                      </>
                    )
                  }}
                >
                  <StyledClinTableRow>
                    <ClinTableBodyCell verticalAlign={'top'}>
                      {t('preferences:relavent_marketing_information')}
                    </ClinTableBodyCell>
                    <ClinTableBodyCell verticalAlign={'top'}>
                      <ClinSwitch
                        inputRef={register({})}
                        name={`marketingPreference`}
                        onChange={(event) => {
                          setValue(
                            'marketingPreference',
                            event.currentTarget.checked,
                            {
                              shouldValidate: true
                            }
                          )
                        }}
                        id="marketing-preference"
                        label={t('preferences:relavent_marketing_information')}
                        defaultChecked={preferences?.marketingPreference}
                      />
                    </ClinTableBodyCell>
                  </StyledClinTableRow>
                </ClinTable>
              </Col>
            </Row> */}
          </form>
        </ClinPageContentFrame>
      )}
    </ClinPageContentFrame>
  )
}
